/**
 * feat-235 — the Tracks tab's CENTRE pane: one track's ordering, and its settings.
 *
 * Ordering lives here rather than in the library pane because it is the only part
 * of membership a checkbox cannot express, it needs the most width, and it is
 * literally what the crew's phone renders as "LESSON 1…6". The ordinal column
 * uses that exact wording so what the author sets and what the crew sees are
 * visibly the same list. The ordinal is TRACK-RELATIVE (D5 + D6): a shared
 * lesson has a position per track, and moving it here never moves it elsewhere.
 */

import React, { useEffect, useState } from 'react';
import type {
  LearnTrack,
  QuizUnit,
  TrackAccessMode,
} from '../../../../services/quizContentAdminService';
import { lessonImagePublicUrl } from '../../../../services/quizContentAdminService';
import { QUIZ_TERMS, termCount } from '../../quizTerminology';
import { TrackArtControl } from './TrackArtControl';
import { TrackSwatch } from './TrackSwatch';
import { moveBefore, prereqOptions } from './trackGraph';
import { formatReleaseInstant, isScheduledLater } from '../lessonSchedule';

export type TrackSubtab = 'lessons' | 'settings';

/**
 * feat-238 — the non-colour half of the Lesson status pill, shared by BOTH
 * variants so `On` and `Hidden` cannot drift apart in width (AC13).
 *
 * Colour, radius, font size, padding and both labels are inherited byte-for-byte
 * from the Lessons tab (`MasterDetailTree.tsx:225-229`) — matching it is the
 * point of the feature. feat-238 therefore inherited that tab's AA contrast
 * shortfall too, deliberately, rather than fork the vocabulary by fixing one
 * surface of six.
 *
 * feat-240 closed it across all seven surfaces at once: the colour pair is now
 * `bg-success-light` / `text-success-on` (4.62:1) and `bg-gray-100` /
 * `text-inkMuted` (4.77:1), both clearing AA for this `text-[10px]` size. The
 * byte-for-byte rule is UNCHANGED and still binding — it is simply matching a
 * pair that now passes. Do not "improve" the colours here alone; that is the
 * fork feat-238 was avoiding, and it is still a fork.
 *
 * `min-w-[48px] text-center` is the one addition: the row's title is
 * `flex-1 truncate`, so everything right of it packs against the row edge and an
 * auto-width pill would shove the drill count sideways by ~19.9px on every
 * status change. 48px is Tailwind's own 3rem step and clears the measured
 * `Hidden` label (45.56px). `min-w-` rather than `w-12` because a system-ui
 * fallback renders `Hidden` at 48.72px and a hard width would clip the word.
 *
 * This only works while the pill is a DIRECT child of the row's flex container:
 * `min-width` and `text-align` do not apply to a non-replaced inline box, so
 * wrapping it in a plain <span> silently disables both.
 */
const LESSON_STATUS_PILL =
  'text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0 min-w-[48px] text-center';

/** The fields this pane can patch. `is_default` is absent on purpose — which
 *  track is the implicit one is decided by migration 114 and enforced by
 *  `uq_learn_track_default`, not by an editor control. */
export interface TrackPatch {
  title?: string;
  description?: string | null;
  access_mode?: TrackAccessMode;
  image_s3_key?: string | null;
  completed_image_s3_key?: string | null;
  active?: boolean;
}

export interface TrackDetailPaneProps {
  tenantId: string;
  track: LearnTrack;
  tracks: LearnTrack[];
  /** The tenant's whole lesson library, for resolving membership ids to rows. */
  unitsById: Record<string, QuizUnit>;
  glyphForKey: (key: string) => string;
  subtab: TrackSubtab;
  onSubtab: (t: TrackSubtab) => void;
  onPatch: (patch: TrackPatch) => Promise<void>;
  onDelete: () => void;
  onReorderLessons: (orderedLessonIds: string[]) => void;
  onRemoveLesson: (lessonId: string) => void;
  onSetPrereqs: (requiredTrackIds: string[]) => void;
  busy?: boolean;
}

/** Lesson row glyph: uploaded image → mascot → emoji → 📚 (the feat-108/187
 *  precedence, matching the Lessons tab's navigator). */
const LessonGlyph: React.FC<{ unit: QuizUnit | undefined; glyphForKey: (k: string) => string }> = ({
  unit,
  glyphForKey,
}) => {
  const url = unit ? lessonImagePublicUrl(unit.image_s3_key) : null;
  if (url) return <img src={url} alt="" className="w-5 h-5 rounded object-cover shrink-0" />;
  const glyph = unit?.mascot_key ? glyphForKey(unit.mascot_key) : unit?.emoji || '📚';
  return (
    <span className="w-5 h-5 rounded-md bg-white border border-softBorder grid place-items-center text-[11px] shrink-0">
      {glyph}
    </span>
  );
};

export const TrackDetailPane: React.FC<TrackDetailPaneProps> = ({
  tenantId,
  track,
  tracks,
  unitsById,
  glyphForKey,
  subtab,
  onSubtab,
  onPatch,
  onDelete,
  onReorderLessons,
  onRemoveLesson,
  onSetPrereqs,
  busy = false,
}) => {
  const [title, setTitle] = useState(track.title);
  const [description, setDescription] = useState(track.description ?? '');
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => {
    setTitle(track.title);
    setDescription(track.description ?? '');
  }, [track.id, track.title, track.description]);

  const reorder = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const next = moveBefore(track.lesson_ids, dragId, targetId);
    if (next !== track.lesson_ids) onReorderLessons(next);
  };

  const options = prereqOptions(track, tracks);

  return (
    <section className="flex flex-col min-h-0 overflow-hidden">
      <div className="px-6 pt-6 pb-4 shrink-0 space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-ink font-bold">
            <span className="text-inkMuted/60 text-xs mr-1 font-normal">{QUIZ_TERMS.track.singular}</span>
            {track.title || '(untitled)'}
          </span>
        </div>

        <div className="bg-cream/70 border border-softBorder rounded-2xl p-3 flex items-center gap-3 flex-wrap">
          <TrackSwatch
            title={track.title}
            imageKey={track.image_s3_key}
            testId={`track-meta-swatch-${track.id}`}
            className="w-12 h-12 rounded-xl border border-softBorder"
            monogramClassName="text-base"
          />
          <input
            value={title}
            disabled={busy}
            aria-label={`${QUIZ_TERMS.track.singular} title`}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              const next = title.trim();
              if (next && next !== track.title) onPatch({ title: next });
              else setTitle(track.title);
            }}
            className="flex-1 min-w-[180px] bg-transparent font-display text-lg font-semibold px-1 py-1 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-coral/40"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => onPatch({ active: !track.active })}
            className={`text-xs font-bold rounded-full px-2.5 py-1 flex items-center gap-1.5 ${
              track.active ? 'bg-success-light text-success-on' : 'bg-gray-100 text-inkMuted'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${track.active ? 'bg-success' : 'bg-inkMuted'}`} />
            {track.active ? 'Active' : 'Hidden'}
          </button>
          {track.is_default ? (
            // Disabled, not rejected: the API answers 409 and the button never
            // gets to send it. The hover line says what to do instead, because a
            // dead control with no reason reads as a bug.
            <span
              className="text-sm font-semibold text-inkMuted/60 line-through cursor-not-allowed"
              title={`${track.title || '(untitled)'} is where ${QUIZ_TERMS.lesson.plural} live when they aren't in a role ${QUIZ_TERMS.track.singular}. Rename it or hide it instead.`}
            >
              Delete {QUIZ_TERMS.track.singular}
            </span>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={onDelete}
              className="text-coral text-sm font-semibold hover:underline disabled:opacity-50"
            >
              Delete {QUIZ_TERMS.track.singular}
            </button>
          )}
          {track.is_default && (
            <span className="bg-preview-light text-preview text-[10px] font-bold rounded-full px-1.5 py-0.5">
              Default {QUIZ_TERMS.track.singular.toLowerCase()}
            </span>
          )}
        </div>

        <div className="flex gap-5 border-b border-softBorder -mb-4">
          {(['lessons', 'settings'] as TrackSubtab[]).map((key) => (
            <button
              key={key}
              type="button"
              aria-selected={subtab === key}
              role="tab"
              onClick={() => onSubtab(key)}
              className={`text-[13px] font-bold pb-2.5 -mb-px border-b-2 ${
                subtab === key ? 'text-ink border-ink' : 'text-inkMuted border-transparent'
              }`}
            >
              {key === 'lessons' ? (
                <>
                  {QUIZ_TERMS.lesson.plural}{' '}
                  <span className="text-inkMuted font-semibold">{track.lesson_ids.length}</span>
                </>
              ) : (
                'Settings'
              )}
            </button>
          ))}
        </div>
      </div>

      {/* the ONLY scrolling region of this pane */}
      <div className="px-6 pb-6 pt-4 flex-1 min-h-0 overflow-y-auto">
        {subtab === 'lessons' ? (
          track.lesson_ids.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-sm font-bold">
                No {QUIZ_TERMS.lesson.plural} in {track.title || '(untitled)'} yet.
              </div>
              <div className="text-xs text-inkMuted mt-1">
                Tick {QUIZ_TERMS.lesson.plural} on the right and add them — they land at the end, and
                you can drag to re-position after.
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {track.lesson_ids.map((lessonId, index) => {
                const unit = unitsById[lessonId];
                return (
                  <div
                    key={lessonId}
                    data-testid={`membership-row-${lessonId}`}
                    draggable
                    onDragStart={() => setDragId(lessonId)}
                    onDragEnd={() => setDragId(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => reorder(lessonId)}
                    // feat-238 — a hidden Lesson dims its whole row, matching the
                    // Lessons tab (MasterDetailTree.tsx:217-219). Gated on `unit`,
                    // not on `unit?.active`: an unresolvable id is UNKNOWN, not
                    // hidden, and must not be dimmed (AC9).
                    className={`group flex items-center gap-2.5 px-2.5 py-2 bg-white border border-softBorder rounded-xl text-[13.5px] ${
                      unit && !unit.active ? 'opacity-60' : ''
                    }`}
                  >
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-inkMuted/50 text-xs select-none cursor-grab">
                      ⠿
                    </span>
                    <span className="text-[10px] font-extrabold text-inkMuted tracking-wide w-20 shrink-0 tabular-nums">
                      {QUIZ_TERMS.lesson.singular.toUpperCase()} {index + 1}
                    </span>
                    <LessonGlyph unit={unit} glyphForKey={glyphForKey} />
                    <span className="flex-1 truncate font-bold">
                      {unit ? unit.title || '(untitled)' : lessonId}
                    </span>
                    <span className="text-[11px] text-inkMuted shrink-0">
                      {termCount(unit?.drills.length ?? 0, 'drill')}
                    </span>
                    {/* feat-238 — read-only status, NOT a control: no handler, no
                        <button>, and outside the remove button's hit area (AC7).
                        Real text, not aria-hidden, because status has no text
                        equivalent elsewhere on the row (AC10) — unlike the
                        library pane's dots, which the row `title` already says.
                        No pill at all when the id does not resolve (AC9). */}
                    {/* feat-256 — the third state, `Later`. THIS is the site
                        feat-238 AC13 was written for: the drill count sits to the
                        left and the `×` to the right, so an auto-width pill shoves
                        the row on every status change. `bg-preview-light
                        text-preview` is the only change; `LESSON_STATUS_PILL` and
                        its `min-w-[48px]` are untouched, and `Later` was measured
                        against the `Hidden` ceiling so the floor keeps governing.
                        Verified on the rendered mockup: all three pills measure
                        exactly 48.00px and neither neighbour moves between states.

                        Still read-only and still not a control (AC7), and still
                        real text rather than `aria-hidden` (AC10) — a colour-only
                        third state is invisible to a screen reader and to a
                        deuteranope, which is why `Later` had to be a WORD that
                        fits rather than a new pill colour alone. `title` is 7.15. */}
                    {unit && (
                      <span
                        data-testid={`membership-row-status-${lessonId}`}
                        title={isScheduledLater(unit) ? `Available ${formatReleaseInstant(unit.available_at)}` : undefined}
                        className={`${
                          !unit.active
                            ? 'bg-gray-100 text-inkMuted'
                            : isScheduledLater(unit)
                              ? 'bg-preview-light text-preview'
                              : 'bg-success-light text-success-on'
                        } ${LESSON_STATUS_PILL}`}
                      >
                        {!unit.active ? 'Hidden' : isScheduledLater(unit) ? 'Later' : 'On'}
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onRemoveLesson(lessonId)}
                      title={`Remove from ${track.title || '(untitled)'}`}
                      aria-label={`Remove ${unit?.title || lessonId} from ${track.title || '(untitled)'}`}
                      className="text-inkMuted hover:text-coral text-base px-1 disabled:opacity-50"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="flex flex-col gap-5">
            <div>
              <label
                htmlFor={`track-description-${track.id}`}
                className="block text-xs font-bold text-inkMuted mb-1.5"
              >
                Description
              </label>
              <textarea
                id={`track-description-${track.id}`}
                value={description}
                disabled={busy}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => {
                  const next = description.trim();
                  if (next !== (track.description ?? '')) onPatch({ description: next || null });
                }}
                placeholder={`What this ${QUIZ_TERMS.track.singular} is for`}
                className="w-full min-h-[64px] text-sm bg-white border border-softBorder rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40"
              />
              <p className="text-[11px] text-inkMuted mt-1">
                Shown under the {QUIZ_TERMS.track.singular} name on the crew's phone. Translated into
                Spanish through the Translations queue.
              </p>
            </div>

            <div>
              <span className="block text-xs font-bold text-inkMuted mb-1.5">
                Who can start this {QUIZ_TERMS.track.singular}
              </span>
              <div className="flex flex-col gap-2">
                {(
                  [
                    {
                      value: 'open' as TrackAccessMode,
                      title: 'Anyone',
                      detail: 'Open from day one. The crew can see it and start it whenever they like.',
                    },
                    {
                      value: 'prereq' as TrackAccessMode,
                      title: `After finishing other ${QUIZ_TERMS.track.plural}`,
                      detail: 'Visible but locked until every one ticked below is complete.',
                    },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex gap-2.5 items-start px-3 py-2.5 border rounded-xl bg-white cursor-pointer ${
                      track.access_mode === opt.value ? 'border-coral bg-coral-light' : 'border-softBorder'
                    }`}
                  >
                    <input
                      type="radio"
                      className="mt-1 accent-coral"
                      name={`access-mode-${track.id}`}
                      value={opt.value}
                      checked={track.access_mode === opt.value}
                      disabled={busy}
                      onChange={() => onPatch({ access_mode: opt.value })}
                    />
                    <span>
                      <span className="block text-[13.5px] font-bold">{opt.title}</span>
                      <span className="block text-xs text-inkMuted">{opt.detail}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* The prerequisite list appears ONLY under the second mode, so an
                `open` track can never carry orphan prereq rows the slice-3
                evaluator would ignore. */}
            {track.access_mode === 'prereq' && (
              <div>
                <span className="block text-xs font-bold text-inkMuted mb-1.5">
                  Must finish first — all of them
                </span>
                <div className="border border-softBorder rounded-xl bg-white p-2.5 flex flex-col gap-1">
                  {options.length <= 1 ? (
                    <p className="text-xs text-inkMuted px-1 py-1">
                      There is no other {QUIZ_TERMS.track.singular} to require yet.
                    </p>
                  ) : (
                    options.map(({ track: candidate, checked, blockedReason }) => (
                      <label
                        key={candidate.id}
                        data-testid={`prereq-row-${candidate.id}`}
                        className={`flex items-center gap-2.5 text-[13px] px-1 py-1 ${
                          blockedReason ? 'text-inkMuted/65' : 'cursor-pointer'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 accent-coral"
                          checked={checked}
                          disabled={!!blockedReason || busy}
                          aria-label={candidate.title || '(untitled)'}
                          onChange={() => {
                            const next = checked
                              ? track.prereq_track_ids.filter((id) => id !== candidate.id)
                              : [...track.prereq_track_ids, candidate.id];
                            onSetPrereqs(next);
                          }}
                        />
                        <span>{candidate.title || '(untitled)'}</span>
                        {!candidate.active && (
                          <span className="bg-gray-100 text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5">
                            Hidden
                          </span>
                        )}
                        {/* Cycles are SHOWN, disabled, with the reason on the row —
                            a hidden option is indistinguishable from a bug, and the
                            author is the person who can fix the actual conflict. */}
                        {blockedReason && (
                          <span className="text-[11px] text-inkMuted ml-auto">{blockedReason}</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
                <p className="text-[11px] text-inkMuted mt-1">
                  Every {QUIZ_TERMS.track.singular} ticked here must be finished. Not any one of them
                  — all of them.
                </p>
              </div>
            )}

            <div>
              <span className="block text-xs font-bold text-inkMuted mb-1.5">Artwork</span>
              <div className="grid grid-cols-2 gap-3">
                <TrackArtControl
                  tenantId={tenantId}
                  trackId={track.id}
                  trackTitle={track.title}
                  caption="While in progress"
                  artLabel="in-progress art"
                  hint="Shown while the crew is working through it"
                  s3Key={track.image_s3_key}
                  onSave={(key) => onPatch({ image_s3_key: key })}
                />
                <TrackArtControl
                  tenantId={tenantId}
                  trackId={track.id}
                  trackTitle={track.title}
                  caption="Once completed"
                  artLabel="completed art"
                  hint="Shown on the crew's phone from a later release"
                  s3Key={track.completed_image_s3_key}
                  emptyNote="Not uploaded — falls back to the in-progress art"
                  onSave={(key) => onPatch({ completed_image_s3_key: key })}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TrackDetailPane;
